/**
 * Pure helpers for ActionOverview.
 *
 * Kept in a separate module (not the component file) so the button-derivation
 * logic is unit-testable in isolation and doesn't trip the
 * `react-refresh/only-export-components` rule that fires when a component file
 * also exports non-components.
 */

import type { FC } from 'react';

import { defaultUrlTransform } from '@/lib/crm/components/MarkdownContent';
import type { SlideoutEntry } from '@/lib/crm/contexts/SlideoutStackContext';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { logger } from '@/lib/logger';
import type { ProposalDetail, TriggerData } from '@/lib/proposals/core/types';
import {
  isCrmEventTrigger,
  isEmailTrigger,
  isHighPriorityTrigger,
} from '@/lib/proposals/core/types';

import { getEntityTypeIcon } from './shared';
import { extractSenderEmail } from './shared/textUtils';

/**
 * Entity types labelled by their display name rather than their type label,
 * and the participant types surfaced from an email thread. Contacts and
 * companies use the display name ("View Ada Lovelace") so multiple people /
 * orgs stay distinguishable at a glance.
 */
const CONTACT_COMPANY_TYPES = new Set(['ahoy_contact', 'ahoy_company']);

/** Where a "View X" button navigates when activated. */
export type TriggerButtonTarget =
  | { kind: 'entity'; entityId: string }
  | { kind: 'url'; url: string };

/** A single collapsed-state "View X" button derived from a trigger. */
export interface TriggerButtonDescriptor {
  /** Stable dedupe key (entity id or external url). */
  key: string;
  /** Button text, e.g. "View Ada Lovelace" or "View meeting summary". */
  label: string;
  /**
   * When set, the button's label is derived from this entity's *display name*
   * (contacts/companies — "View {name}"), so the renderer should prefer the
   * live CRM-store display name over the point-in-time `label` snapshot and
   * fall back to `label` while the store loads (store-first per A1). Undefined
   * for type-labelled buttons ("View meeting summary") — those don't depend on
   * a display name and can never go stale, so they never subscribe to a
   * snapshot. */
  displayNameEntityId?: string;
  /** Leading icon component (from the entity-type icon registry). */
  icon: FC<{ className?: string }>;
  /** Navigation target. */
  target: TriggerButtonTarget;
  /**
   * Entity type behind the button, used for priority ordering (deal >
   * contacts > companies > everything else — see `sortTriggerButtons`).
   */
  entityTypeName: string;
}

/**
 * Active styling for a trigger shortcut whose record is currently open on
 * the slideout stack (inline record panel or overlay slideout). Brand-tint
 * selected state. `cx` is tailwind-merge, so these override the secondary
 * color's bg/text/ring. Shared by the inline TriggerActionButton and the
 * '+N' overflow control (TriggerOverflowButton).
 */
export const OPEN_RECORD_CLASSES =
  'bg-utility-brand-50 text-utility-brand-600 ring-utility-brand-300 hover:bg-utility-brand-50 hover:text-utility-brand-600 *:data-icon:text-utility-brand-600 hover:*:data-icon:text-utility-brand-600';

/**
 * Whether a trigger button's target record is currently open anywhere on
 * the slideout stack (including underneath a further slideout). URL targets
 * are never "open".
 */
export function isTriggerButtonRecordOpen(
  target: TriggerButtonTarget,
  stack: readonly SlideoutEntry[] | undefined
): boolean {
  if (target.kind !== 'entity') {
    return false;
  }
  return !!stack?.some(
    entry => entry.kind === 'entity' && entry.id === target.entityId
  );
}

/**
 * Priority tier for header ordering: deals first, then contacts, then
 * companies, then everything else. Lower sorts first.
 */
function triggerButtonTier(entityTypeName: string): number {
  switch (entityTypeName) {
    case 'ahoy_deal':
      return 0;
    case 'ahoy_contact':
      return 1;
    case 'ahoy_company':
      return 2;
    default:
      return 3;
  }
}

/**
 * Order trigger buttons by entity priority (deal > contacts > companies >
 * everything else), stable within a tier. Returns a new array.
 */
export function sortTriggerButtons(
  buttons: TriggerButtonDescriptor[]
): TriggerButtonDescriptor[] {
  return [...buttons].sort(
    (a, b) =>
      triggerButtonTier(a.entityTypeName) - triggerButtonTier(b.entityTypeName)
  );
}

/**
 * Validate and resolve a `kind: 'url'` trigger target before it reaches
 * `window.open`. No current trigger-derivation path produces a `kind: 'url'`
 * target (see `deriveTriggerButtons` — it only ever builds `{ kind: 'entity' }`
 * targets), but the discriminated union and its `window.open` call site both
 * exist in shipped code, so this is the enforcement point for a future
 * producer of `kind: 'url'` targets rather than for any current call site.
 * Reuses the same scheme allowlist `MarkdownContent` applies to markdown
 * links, rejecting `javascript:`/`data:`/`file:` and similar schemes.
 */
/**
 * Whether trigger data is expected for a proposal — a CRM-event source, or
 * any action carrying a source email thread id. Scans the ORIGINAL
 * (unfiltered) action list: hiding no-op update actions must not disable the
 * trigger preview.
 */
export function proposalHasTrigger(proposal: ProposalDetail): boolean {
  return (
    proposal.source_context?.channel === 'crm_event' ||
    proposal.actions.some(
      a =>
        a?.payload != null &&
        'action_params' in a.payload &&
        Boolean(
          (a.payload as { action_params?: { thread_id?: string } })
            .action_params?.thread_id
        )
    )
  );
}

export function resolveTriggerUrl(url: string): string | undefined {
  const resolved = defaultUrlTransform(url);
  if (!resolved) {
    logger.warn('Rejected unsafe trigger button URL', {
      url,
      component: 'ActionOverview',
    });
    return undefined;
  }
  return resolved;
}

/**
 * Build the `View X` label for an entity from a display name. Contacts and
 * companies are labelled by their display name ("View Ada Lovelace"); this is
 * exported so the renderer can rebuild the label from the store-fresh name.
 */
export function displayNameButtonLabel(displayName: string): string {
  return `View ${displayName}`;
}

/**
 * Button text for an entity. Contacts and companies are labelled by their
 * display name ("View Ada Lovelace"); every other type uses its type label
 * ("View meeting summary").
 */
function buttonLabel(entityTypeName: string, displayName: string): string {
  return CONTACT_COMPANY_TYPES.has(entityTypeName)
    ? displayNameButtonLabel(displayName)
    : `View ${getTypeLabelByName(entityTypeName).toLowerCase()}`;
}

/**
 * Build a "View X" button descriptor for a crm entity. Contact/company buttons
 * carry `displayNameEntityId` so the renderer resolves their label store-first;
 * type-labelled buttons omit it.
 */
export function entityButtonDescriptor(
  entityTypeName: string,
  entityId: string,
  displayName: string
): TriggerButtonDescriptor {
  return {
    key: entityId,
    label: buttonLabel(entityTypeName, displayName),
    displayNameEntityId: CONTACT_COMPANY_TYPES.has(entityTypeName)
      ? entityId
      : undefined,
    icon: getEntityTypeIcon(entityTypeName),
    target: { kind: 'entity', entityId },
    entityTypeName,
  };
}

/**
 * Participant email addresses on a proposal's email-carrying triggers,
 * normalized to lowercase and deduped. Two shapes carry raw addresses:
 *
 * - Plain email triggers — `email.from/to/cc` (the inbound email acted on).
 * - Email-thread crm_event triggers — their merged `event` payload carries
 *   `from`/`to`/`cc`. The backend's `participant_entities` on those triggers
 *   only includes participants it has already resolved (often just the org
 *   user), so the raw addresses are the only complete participant source.
 *
 * The hook resolves these against the CRM store's email index
 * (`useStoreResolvedParticipants`) and surfaces the ones that resolve to
 * contacts as "View {name}" buttons.
 */
export function deriveTriggerParticipantEmails(
  triggers: TriggerData[]
): string[] {
  const seen = new Set<string>();
  // `from` (and occasionally to/cc entries) arrive as "Name <email>" —
  // extractSenderEmail handles both that and bare-address forms.
  const add = (raw: unknown) => {
    if (typeof raw !== 'string') {
      return;
    }
    const email = extractSenderEmail(raw).toLowerCase();
    if (email) {
      seen.add(email);
    }
  };
  const addAll = (from: unknown, to: unknown, cc: unknown) => {
    add(from);
    for (const list of [to, cc]) {
      if (Array.isArray(list)) {
        list.forEach(add);
      }
    }
  };

  for (const trigger of triggers) {
    if (isEmailTrigger(trigger)) {
      const { from, to, cc } = trigger.email;
      addAll(from, to, cc);
    } else if (isCrmEventTrigger(trigger)) {
      const { event } = trigger.crm_event;
      addAll(event.from, event.to, event.cc);
    }
  }
  return [...seen];
}

/**
 * Recipient email addresses on a proposal's email-send actions (to + cc),
 * normalized to lowercase and deduped. Email actions carry their recipients
 * in the action payload rather than in trigger data (a new outbound email has
 * no email trigger at all), so this is the second source of "contacts on
 * those emails" for the header's trigger shortcut buttons.
 */
export function deriveActionRecipientEmails(
  proposal: ProposalDetail
): string[] {
  const seen = new Set<string>();
  for (const action of proposal.actions) {
    if (action?.payload == null || !('action_params' in action.payload)) {
      continue;
    }
    const params = (
      action.payload as {
        action_params?: { to?: unknown; cc?: unknown };
      }
    ).action_params;
    if (!params) {
      continue;
    }
    for (const list of [params.to, params.cc]) {
      if (!Array.isArray(list)) {
        continue;
      }
      for (const raw of list) {
        if (typeof raw !== 'string') {
          continue;
        }
        const email = extractSenderEmail(raw).toLowerCase();
        if (email) {
          seen.add(email);
        }
      }
    }
  }
  return [...seen];
}

/**
 * Derive the "View X" shortcut buttons from a proposal's triggers. The buttons
 * are the only rendering of standard triggers in the Action Overview (trigger
 * cards never render inside it), so every standard trigger must yield a
 * button.
 *
 * - High-priority triggers (see `isHighPriorityTrigger`) render as their own
 *   card below the overview and get no button of their own; their
 *   contact/company participants (e.g. an email thread's recipients) become
 *   quick buttons instead.
 * - Every other crm_event trigger becomes one button to open that entity.
 *
 * Contacts/companies are labelled by display name; other types by type label.
 * Deduped by target key; uncapped.
 */
/**
 * The first CRM-event trigger whose entity is an email thread, or null.
 * Email-thread triggers are high priority (they render as their own card and
 * deliberately get NO header button — see deriveTriggerButtons), so when the
 * header ends up with no trigger buttons at all this is the source context
 * AutoOpenFirstRecord falls back to opening.
 */
export function deriveFirstEmailThreadTriggerEntityId(
  triggers: TriggerData[]
): string | null {
  for (const trigger of triggers) {
    if (
      isCrmEventTrigger(trigger) &&
      // Matches HIGH_PRIORITY_TRIGGER_ENTITY_TYPES' email-thread entry.
      trigger.crm_event.entity_type_name === 'ahoy_email_thread'
    ) {
      return trigger.crm_event.entity_id;
    }
  }
  return null;
}

export function deriveTriggerButtons(
  triggers: TriggerData[]
): TriggerButtonDescriptor[] {
  const out: TriggerButtonDescriptor[] = [];
  const seen = new Set<string>();

  const add = (descriptor: TriggerButtonDescriptor) => {
    if (!seen.has(descriptor.key)) {
      seen.add(descriptor.key);
      out.push(descriptor);
    }
  };

  for (const trigger of triggers) {
    // Plain email triggers render as their own card below the overview — no
    // button. (Non-crm triggers are exactly the plain email triggers.)
    if (!isCrmEventTrigger(trigger)) {
      continue;
    }

    const { entity_type_name, entity_id, display_name, participant_entities } =
      trigger.crm_event;

    if (isHighPriorityTrigger(trigger)) {
      // High-priority triggers render as their own card below the overview, so
      // they get no button of their own — but surface their contact/company
      // participants (e.g. an email thread's recipients).
      if (participant_entities) {
        for (const participant of Object.values(participant_entities)) {
          if (CONTACT_COMPANY_TYPES.has(participant.entity_type_name)) {
            add(
              entityButtonDescriptor(
                participant.entity_type_name,
                participant.entity_id,
                participant.display_name
              )
            );
          }
        }
      }
      continue;
    }

    // Org users never render as buttons — a trigger anchored on a user
    // entity (e.g. an email thread's initiator association pointing at the
    // current user) would surface as a noise "View user" button. The email-
    // participant resolution path enforces the same rule by filtering
    // resolved entities to ahoy_contact.
    if (entity_type_name === 'ahoy_user') {
      continue;
    }

    add(entityButtonDescriptor(entity_type_name, entity_id, display_name));
  }

  return out;
}
